import { Creativeservicev1Type } from 'next-gen-sdk'
import { PreviewType } from '@eltoro-ui/components'

type CreativeValidatorType =
  | {
      type: 'warning' | 'error'
      msg: string
      messages?: string[] // For multiple error messages
    }
  | undefined

// File size limits (in bytes)
const BANNER_MAX_SIZE = 200000 // 200 KB
const BANNER_RECOMMENDED_SIZE = 100000 // 100 KB
const VIDEO_MAX_SIZE = 1000000000 // 1 GB
const VIDEO_RECOMMENDED_SIZE = 100000000 // 100 MB
const OTT_MAX_SIZE = 1000000000 // 1 GB

// Duration limits (in seconds)
const OTT_MAX_DURATION = 90 // seconds
const VIDEO_RECOMMENDED_DURATION = 60 // seconds

// OTT Video requirements
const OTT_MIN_WIDTH = 1920
const OTT_MIN_HEIGHT = 1080
const OTT_MIN_BITRATE = 5000 // kbps

// OTT Video requirements
const OTT_RECOMMENDED_BITRATE = 14000 // kbps

const loadImage = (src: string) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

// For OTT uploads, validate the file's binary signature instead of relying on the
// extension or browser-reported MIME type so renamed MOV files are not accepted as MP4.
// This stricter check is limited to OTT because VIDEO intentionally allows MOV uploads.

const readMagicBytes = (file: File, length: number): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(e.target.result))
      } else {
        reject(new Error('Failed to read file'))
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file.slice(0, length))
  })

// Detects if a file is truly an MP4 by scanning its boxes for an ftyp major brand.
// MOV (QuickTime) files use 'qt  ' as the major brand even when renamed to .mp4.
// Scans the first SCAN_BYTES rather than assuming ftyp starts at byte offset 4,
// since some containers (e.g. those with a preceding 'wide' or 'free' box) place
// ftyp later. Fails closed: returns false when ftyp cannot be located or read.
const FTYP_SCAN_BYTES = 512
const isTrulyMp4 = async (file: File): Promise<boolean> => {
  try {
    const bytes = await readMagicBytes(file, FTYP_SCAN_BYTES)
    const view = new DataView(bytes.buffer)
    const decoder = new TextDecoder('ascii')

    let offset = 0
    while (offset + 8 <= bytes.length) {
      const boxSize = view.getUint32(offset, false) // big-endian
      const boxType = decoder.decode(bytes.slice(offset + 4, offset + 8))

      if (boxType === 'ftyp') {
        if (offset + 12 > bytes.length) return false // can't read brand — fail closed
        const majorBrand = decoder.decode(bytes.slice(offset + 8, offset + 12))
        return majorBrand !== 'qt  '
      }

      // A size of 0 means the box runs to EOF; size of 1 means 64-bit extended
      // size follows — neither is expected this early, so stop scanning.
      if (boxSize < 8) break
      offset += boxSize
    }

    // ftyp not found in scanned region — cannot confirm valid MP4 container
    return false
  } catch {
    return false // fail closed on read error
  }
}

const loadVideoDimensions = (src: string) =>
  new Promise((resolve) => {
    const video = document.createElement('video')
    video.addEventListener(
      'loadeddata',
      () => {
        const { duration, videoHeight, videoWidth } = video
        const height = videoHeight
        const width = videoWidth

        resolve({ duration, height, width })
      },
      false,
    )
    video.src = src
  })

export const creativeValidator = async (
  file: PreviewType,
  uploadType: Creativeservicev1Type,
): Promise<CreativeValidatorType> => {
  const suggestedBannerDimensions = [
    '300x250',
    '728x90',
    '160x600',
    '320x50',
    '300x600',
  ]

  if (uploadType === 'CREATIVE_TYPE_BANNER') {
    const acceptedType =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/gif'
    if (!acceptedType) {
      return {
        type: 'error',
        msg: 'Must be a "JPG", "PNG" or "GIF" Image file.',
      }
    }
    if (file.size > BANNER_MAX_SIZE) {
      return { type: 'error', msg: 'File size cannot be greater than 200 KB.' }
    }
    // Load image here because height/width from uploader keeps coming back undefined?
    const image = (await loadImage(
      window.URL.createObjectURL(file),
    )) as HTMLImageElement

    if (image.width && image.height) {
      // if both too big + wrong dimensions
      if (
        file.size > BANNER_RECOMMENDED_SIZE &&
        !suggestedBannerDimensions.find(
          (s) => s === `${file.width}x${file.height}`,
        )
      ) {
        return {
          type: 'warning',
          msg:
            'Your creative file size is above the recommended size of 100 KB and is not in the top 5 recommended sizes. This may limit our serving capacity.',
        }
      }
      // if is not in top 5 creative dimension size
      if (
        !suggestedBannerDimensions.find(
          (s) => s === `${image.width}x${image.height}`,
        )
      ) {
        return {
          type: 'warning',
          msg:
            'Your creative is not in the top 5 recommended sizes. This may limit our serving capacity.',
        }
      }
    }
    // if is bigger than 100000 (recommended)
    if (file.size > BANNER_RECOMMENDED_SIZE) {
      return {
        type: 'warning',
        msg:
          'Your creative file size is above the recommended size of 100 KB. This may limit our serving capacity.',
      }
    }

    return undefined
  }
  if (uploadType === 'CREATIVE_TYPE_HTML5') {
    return undefined
  }
  if (uploadType === 'CREATIVE_TYPE_VIDEO') {
    if (file.size > VIDEO_MAX_SIZE) {
      return { type: 'error', msg: 'File size cannot be greater than 1 GB.' }
    }
    const fileExt = file.name
      .substring(file.name.lastIndexOf('.') + 1)
      .toLowerCase()
    const acceptedType =
      file.type === 'video/mp4' ||
      file.type === 'video/mpg' ||
      file.type === 'video/avi' ||
      file.type === 'video/mov'
    if (!acceptedType || fileExt === 'm4v') {
      return {
        type: 'error',
        msg: 'Must be an "MP4", "MPG", "MOV" or "AVI" Image file.',
      }
    }
    const videoDimensions = (await loadVideoDimensions(
      window.URL.createObjectURL(file),
    )) as { duration: number; width: number; height: number }
    if (
      videoDimensions.duration &&
      videoDimensions.duration > VIDEO_RECOMMENDED_DURATION
    ) {
      return {
        type: 'warning',
        msg: 'Duration is greater than the recommended 60 seconds.',
      }
    }
    if (file.size > VIDEO_RECOMMENDED_SIZE) {
      return {
        type: 'warning',
        msg: 'File size is greater than the recommended 100 MB.',
      }
    }
    return undefined
  }
  if (uploadType === 'CREATIVE_TYPE_OTT') {
    const fileExt = file.name
      .substring(file.name.lastIndexOf('.') + 1)
      .toLowerCase()
    const videoDimensions = (await loadVideoDimensions(
      window.URL.createObjectURL(file),
    )) as { duration: number; width: number; height: number }

    const errors: string[] = []
    const warnings: string[] = []

    // Check file size
    if (file.size > OTT_MAX_SIZE) {
      errors.push('File size cannot be greater than 1 GB.')
    }

    // Check file type — also inspect magic bytes to catch renamed .mov files
    if (file.type !== 'video/mp4' || fileExt === 'm4v') {
      errors.push('Unsupported file type. Only MP4 is allowed.')
    } else if (!(await isTrulyMp4(file))) {
      errors.push(
        'File could not be verified as a valid MP4. It may be a QuickTime (.mov) file renamed to .mp4. Please upload a true MP4 file.',
      )
    }

    if (videoDimensions.duration) {
      const kbps = ((file.size / videoDimensions.duration) * 8) / 1000

      // Check duration
      if (videoDimensions.duration >= OTT_MAX_DURATION) {
        errors.push('Duration cannot be greater than 90 seconds.')
      }

      // Check dimensions
      if (
        videoDimensions.width < OTT_MIN_WIDTH ||
        videoDimensions.height < OTT_MIN_HEIGHT
      ) {
        errors.push('Dimensions cannot be smaller than 1920 x 1080.')
      }

      // Check for exactly 16:9 aspect ratio
      const aspectRatio = videoDimensions.width / videoDimensions.height
      const targetAspectRatio = 16 / 9
      const aspectRatioTolerance = 0.01
      if (Math.abs(aspectRatio - targetAspectRatio) > aspectRatioTolerance) {
        errors.push('Aspect ratio must be exactly 16:9.')
      }
      // Check bitrate
      if (kbps < OTT_MIN_BITRATE) {
        errors.push(
          `Bitrate must be at least ${OTT_MIN_BITRATE.toLocaleString()} kbps.`,
        )
      } else if (kbps < OTT_RECOMMENDED_BITRATE) {
        warnings.push(
          `Bitrate is less than the recommended ${OTT_RECOMMENDED_BITRATE.toLocaleString()} kbps.`,
        )
      }

      // Check duration recommendation (15-30 seconds)
      if (videoDimensions.duration < 15 || videoDimensions.duration > 30) {
        warnings.push('Duration is not the recommended 15-30 seconds.')
      }
    }

    // Return errors if any exist
    if (errors.length > 0) {
      return {
        type: 'error',
        msg: errors.join(' '),
        messages: errors,
      }
    }

    // Return warnings if any exist
    if (warnings.length > 0) {
      return {
        type: 'warning',
        msg: warnings.join(' '),
        messages: warnings,
      }
    }

    return undefined
  }
  return undefined
}
